import { Inject, Injectable } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../interfaces/config.interface';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ConfigConstants } from '../config/config-constants';
import { RecipientsInstanceToken, RecipientsInterface } from '../interfaces/recipients.interface';
import { DataMaintenanceLocationModel } from '../models/complex/data-maintenance-location.model';
import { DataMaintenanceSystemConfigModel } from '../models/complex/data-maintenance-system-config.model';
import { RecipientModel } from '../models/low-level/recipient.model';
import { WindowService } from '../services/window/window.service';
import { isDefined } from './is-defined';

@Injectable({
    providedIn: 'root',
})
export class DataMaintenanceUtilities {
    private window: Window;

    public constructor(
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(RecipientsInstanceToken) private readonly recipientsService: RecipientsInterface,
        private readonly translate: TranslateService,
        private readonly windowService: WindowService,
    ) {
        this.window = windowService.nativeWindow;
    }

    public isEmailInvalid(email: string, pattern: string): boolean {
        return isDefined(email) && email.length > 0 && email.match(pattern) === null;
    }

    public updateRecipientListInLocalStorage(model: DataMaintenanceLocationModel): void {
        model.recipients.value.map((recipient) => {
            recipient.isDeletable = !recipient.isFromEmailSet;
            recipient.isEditable = !recipient.isFromEmailSet;
            recipient.isSubmitted = true;
        });
        const cseEmail = this.configService.getCseEmailFax();
        model.recipients.value.unshift(
            new RecipientModel(
                cseEmail,
                ConfigConstants.confirmationServiceReportCommCategory,
                true,
                true,
                false,
                false,
                false,
                true,
            ),
        );
        this.recipientsService
            .saveRecipientsByNotificationId(model.recipients.value, model.notificationIdentifier)
            .pipe(take(1))
            .subscribe();
    }

    public openMailToSubmitData(
        model: DataMaintenanceLocationModel | DataMaintenanceSystemConfigModel,
        oldModel: DataMaintenanceLocationModel | DataMaintenanceSystemConfigModel,
        isInvitro: boolean,
    ): void {
        const emailId = isInvitro
            ? this.configService.getDataManagementEmailInvitro()
            : this.configService.getDataManagementEmail();

        // Subject: Change data from PACE: Username | 60000025627(0001) | 004-526178
        const emailSubject = `${<string>(
            this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_EMAIL_SUBJECT')
        )} | ${
            model.name1.value
        } | ${model.notificationIdentifier.getQmnum()}(${model.notificationIdentifier.getManum()}) | ${
            model.functionalLoc.value
        }`;

        const emailBody = this.getModelValueForMail(model, oldModel);

        const newWindow = this.window.open(
            `mailTo:${emailId}?` +
                `subject=${encodeURIComponent(emailSubject)}` +
                `&body=${encodeURIComponent(emailBody)}`,
            '_blank',
        );
        if (newWindow) {
            newWindow.opener = null;
        }
    }

    public getModelValueForMail(
        model: DataMaintenanceLocationModel | DataMaintenanceSystemConfigModel,
        old: DataMaintenanceLocationModel | DataMaintenanceSystemConfigModel,
    ): string {
        let oldValues = '';
        let newValues = '';

        if (model instanceof DataMaintenanceLocationModel && old instanceof DataMaintenanceLocationModel) {
            const newModel = model.getOutModel();
            const oldModel = old.getOutModel();

            // Only changed value should be sent in mail, so it is necessary to compare old Model values and new Model values
            for (const key in newModel) {
                if (oldModel[key]?.toString().trim() !== newModel[key]?.toString().trim()) {
                    newValues = newValues + `${key} : ${newModel[key]}\n`;
                    oldValues = oldValues + `${key} : ${oldModel[key] ?? '-'}\n`;
                }
            }
            // This below function when an email is deleted from existing model
            // Example: we have Email 1 and Email 2, If Email 2 is deleted then it will be present in newModel
            for (const key in oldModel) {
                if (!newModel.hasOwnProperty(key)) {
                    newValues = newValues + `${key} : -\n`;
                    oldValues = oldValues + `${key} : ${oldModel[key] ?? '-'}\n`;
                }
            }
            return `Old Address:\n` + `${oldValues}\n` + `New Address : \n${newValues}`;
        }
        const newEquipmentModel = <DataMaintenanceSystemConfigModel>model;
        const oldEquipmentModel = <DataMaintenanceSystemConfigModel>old;

        oldEquipmentModel.equipmentSet.value.forEach((oldModel) => {
            newEquipmentModel.equipmentSet.value.forEach((newModel) => {
                if (
                    oldModel.equnr.value === newModel.equnr.value &&
                    (oldModel.tidnr.value.trim() !== newModel.tidnr.value.trim() ||
                        oldModel.matnr.value.trim() !== newModel.matnr.value.trim() ||
                        oldModel.sernr.value.trim() !== newModel.sernr.value.trim())
                ) {
                    oldValues =
                        oldValues +
                        `\nEquipment Name: ${oldModel.equnr.value}\n${oldModel.tidnr.name} : ${oldModel.tidnr.value}\n${oldModel.matnr.name} : ${oldModel.matnr.value}\n${oldModel.sernr.name} : ${oldModel.sernr.value}\n`;
                    newValues =
                        newValues +
                        `\nEquipment Name: ${oldModel.equnr.value}\n${newModel.tidnr.name} : ${newModel.tidnr.value}\n${newModel.matnr.name} : ${newModel.matnr.value}\n${newModel.sernr.name} : ${newModel.sernr.value}\n`;
                }
            });
        });
        return `Old Equipment:\n` + `${oldValues}\n` + `New Equipment :\n${newValues}`;
    }
}
